import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { getProducts, getReorderData } from '@/lib/api';
import { ShoppingCart, Package, ArrowRight, Clock, LayoutGrid, RefreshCw } from 'lucide-react';

export default function ProDashboard() {
  const { user, isPending } = useAuth();
  const { addToCart } = useCart();
  const [reorderProducts, setReorderProducts] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getReorderData().catch(() => ({ data: { products: [] } })),
      getProducts({ limit: 8 }).catch(() => ({ data: { products: [] } }))
    ]).then(([reorder, prods]) => {
      setReorderProducts(reorder.data.products || []);
      setRecommended(prods.data.products || []);
      setLoading(false);
    });
  }, []);

  if (isPending) {
    return (
      <div data-testid="pending-dashboard" className="min-h-[70vh] flex items-center justify-center py-12 px-4">
        <div className="max-w-md text-center">
          <Clock size={48} className="text-[#F5C400] mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-[#111] mb-2">Compte en attente de validation</h1>
          <p className="text-sm text-black/60 mb-6">Votre compte est en cours de validation par notre équipe. Vous recevrez un email dès qu'il sera activé.</p>
          <Link to="/catalog" className="text-sm text-[#00A5FF] font-semibold hover:underline">Parcourir le catalogue</Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = async (productId) => {
    try { await addToCart(productId); } catch {}
  };

  return (
    <div data-testid="pro-dashboard" className="min-h-screen">
      <div className="border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00A5FF] font-semibold mb-2">Espace professionnel</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111] tracking-tight">
            Bonjour, {user?.company_name}
          </h1>
          {user?.discount_percent > 0 && (
            <p className="text-sm text-[#F5C400] font-semibold mt-1">Remise: -{user.discount_percent}%</p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10 mb-8">
          <Link to="/catalog" className="bg-white p-4 flex items-center gap-3 hover:shadow-md transition-shadow duration-200">
            <LayoutGrid size={20} className="text-[#00A5FF]" />
            <span className="text-sm font-semibold">Catalogue</span>
          </Link>
          <Link to="/orders" className="bg-white p-4 flex items-center gap-3 hover:shadow-md transition-shadow duration-200">
            <Package size={20} className="text-[#E8231B]" />
            <span className="text-sm font-semibold">Mes commandes</span>
          </Link>
          <Link to="/account" className="bg-white p-4 flex items-center gap-3 hover:shadow-md transition-shadow duration-200">
            <RefreshCw size={20} className="text-[#F5C400]" />
            <span className="text-sm font-semibold">Mon compte</span>
          </Link>
          <Link to="/contact" className="bg-white p-4 flex items-center gap-3 hover:shadow-md transition-shadow duration-200">
            <Clock size={20} className="text-black/40" />
            <span className="text-sm font-semibold">Support</span>
          </Link>
        </div>

        {/* Quick Reorder */}
        {reorderProducts.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-extrabold text-[#111]">Recommander rapidement</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
              {reorderProducts.slice(0, 8).map((p) => (
                <div key={p.id} className="bg-white p-4 flex flex-col">
                  <p className="text-xs text-[#00A5FF] font-semibold mb-1">{p.category}</p>
                  <p className="text-sm font-bold text-[#111] line-clamp-2 mb-2 flex-1">{p.name}</p>
                  <div className="flex items-center justify-between mt-auto pt-2 border-t border-black/5">
                    <span className="text-sm font-extrabold">{p.pro_price?.toFixed(2)} EUR</span>
                    <button onClick={() => handleAddToCart(p.id)} className="p-2 bg-[#00A5FF] text-white rounded-sm hover:bg-[#0090e0] transition-colors duration-200">
                      <ShoppingCart size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recommendations placeholder */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-extrabold text-[#111]">Suggestions pour vous</h2>
            <Link to="/catalog" className="text-sm text-[#00A5FF] font-medium hover:underline flex items-center gap-1">
              Tout voir <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
            {recommended.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="bg-white p-4 group hover:shadow-md transition-shadow duration-200">
                <div className="aspect-square bg-[#F8F9FA] border border-black/5 mb-3 flex items-center justify-center overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <Package size={30} className="text-black/10" />
                  )}
                </div>
                <p className="text-xs text-[#00A5FF] font-semibold mb-1">{p.category}</p>
                <p className="text-sm font-bold text-[#111] group-hover:text-[#00A5FF] line-clamp-2 mb-2">{p.name}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-extrabold">{(p.pro_price || p.public_price)?.toFixed(2)}</span>
                  <span className="text-xs text-black/50">EUR</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
